import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, Filter, Search, X } from 'lucide-react';
import MainHeader from '../components/MainHeader';
import MainFooter from '../components/MainFooter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function Articles() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState({});
  const [totalArticles, setTotalArticles] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  
  const selectedCategory = searchParams.get('category') || '';
  const searchQuery = searchParams.get('search') || '';
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const articlesPerPage = 12;

  useEffect(() => {
    fetchArticles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, currentPage, searchQuery]);

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const skip = (currentPage - 1) * articlesPerPage;
      let url = `${API_URL}/api/articles?limit=${articlesPerPage}&skip=${skip}`;
      if (selectedCategory) {
        url += `&category=${encodeURIComponent(selectedCategory)}`;
      }
      if (searchQuery) {
        url += `&search=${encodeURIComponent(searchQuery)}`;
      }
      
      const response = await fetch(url);
      const data = await response.json();
      setArticles(data.articles || []);
      setCategories(data.categories || {});
      setTotalArticles(data.total || 0);
    } catch (err) {
      setError('Failed to load articles');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const trimmedSearch = searchInput.trim();
    const newParams = new URLSearchParams(searchParams);
    if (trimmedSearch) {
      newParams.set('search', trimmedSearch);
    } else {
      newParams.delete('search');
    }
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const clearSearch = () => {
    setSearchInput('');
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('search');
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const handleCategoryChange = (category) => {
    const newParams = new URLSearchParams(searchParams);
    if (category === selectedCategory) {
      newParams.delete('category');
    } else {
      newParams.set('category', category);
    }
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const handlePageChange = (page) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', page.toString());
    setSearchParams(newParams);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const totalPages = Math.ceil(totalArticles / articlesPerPage);

  return (
    <div className="min-h-screen bg-[#F9FAFB]" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <MainHeader />
      
      <main className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Hero */}
          <div className="text-center mb-12">
            <p className="text-[11px] font-bold text-[#2F80ED] uppercase tracking-[1.2px] mb-4">
              RESOURCES
            </p>
            <h1 className="text-[40px] font-bold text-[#102A43] mb-4 leading-tight">
              AI Governance Insights
            </h1>
            <p className="text-[18px] text-[#64748B] max-w-2xl mx-auto">
              Practical guides, templates, and best practices for AI accountability and governance.
            </p>
          </div>

          {/* Search Bar */}
          <div className="mb-8">
            <form onSubmit={handleSearch} className="max-w-xl mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#94A3B8]" size={20} />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search articles by topic, keyword, or question..."
                  className="w-full pl-12 pr-24 py-3 border border-[#E2E8F0] rounded-full text-[15px] text-[#102A43] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#2F80ED]/20 focus:border-[#2F80ED] transition-all"
                  data-testid="article-search-input"
                />
                {searchInput && (
                  <button
                    type="button"
                    onClick={clearSearch}
                    className="absolute right-20 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#64748B] p-1"
                    data-testid="clear-search-btn"
                  >
                    <X size={18} />
                  </button>
                )}
                <button
                  type="submit"
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-[#2F80ED] text-white px-4 py-1.5 rounded-full text-[13px] font-medium hover:bg-[#2563eb] transition-colors"
                  data-testid="article-search-btn"
                >
                  Search
                </button>
              </div>
            </form>
            {searchQuery && (
              <div className="text-center mt-3">
                <span className="text-[14px] text-[#64748B]">
                  Results for &ldquo;<span className="font-medium text-[#102A43]">{searchQuery}</span>&rdquo;
                </span>
                <button
                  onClick={clearSearch}
                  className="ml-2 text-[#2F80ED] text-[14px] hover:underline"
                  data-testid="clear-search-results"
                >
                  Clear search
                </button>
              </div>
            )}
          </div>

          {/* Category Filter */}
          {Object.keys(categories).length > 0 && (
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-3">
                <Filter size={16} className="text-[#64748B]" />
                <span className="text-[14px] font-medium text-[#64748B]">Filter by category:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleCategoryChange('')}
                  className={`px-4 py-2 rounded-full text-[13px] font-medium transition-colors ${
                    !selectedCategory 
                      ? 'bg-[#2F80ED] text-white' 
                      : 'bg-white border border-[#E2E8F0] text-[#64748B] hover:border-[#2F80ED] hover:text-[#2F80ED]'
                  }`}
                  data-testid="filter-all"
                >
                  All ({Object.values(categories).reduce((a, b) => a + b, 0)})
                </button>
                {Object.entries(categories).map(([category, count]) => (
                  <button
                    key={category}
                    onClick={() => handleCategoryChange(category)}
                    className={`px-4 py-2 rounded-full text-[13px] font-medium transition-colors ${
                      selectedCategory === category 
                        ? 'bg-[#2F80ED] text-white' 
                        : 'bg-white border border-[#E2E8F0] text-[#64748B] hover:border-[#2F80ED] hover:text-[#2F80ED]'
                    }`}
                    data-testid={`filter-${category.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {category} ({count})
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Results count */}
          {!loading && (
            <p className="text-[14px] text-[#64748B] mb-6">
              Showing {articles.length} of {totalArticles} articles
              {selectedCategory && ` in "${selectedCategory}"`}
              {searchQuery && ` matching "${searchQuery}"`}
            </p>
          )}

          {loading && (
            <div className="text-center py-12">
              <p className="text-[#64748B]">Loading articles...</p>
            </div>
          )}

          {error && (
            <div className="text-center py-12">
              <p className="text-red-600">{error}</p>
            </div>
          )}

          {!loading && !error && articles.length === 0 && (
            <div className="text-center py-12">
              <p className="text-[#64748B]">
                {searchQuery 
                  ? `No articles found for "${searchQuery}". Try a different search term or browse by category.`
                  : 'No articles found. Try a different category.'}
              </p>
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="mt-4 text-[#2F80ED] font-medium hover:underline"
                >
                  Clear search
                </button>
              )}
            </div>
          )}

          {/* Article Grid */}
          {!loading && articles.length > 0 && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map((article) => (
                <Link 
                  key={article.id} 
                  to={`/articles/${article.slug}`}
                  className="bg-white border border-[#E2E8F0] rounded-[10px] overflow-hidden hover:border-[#2F80ED] hover:shadow-md transition-all block group"
                  data-testid={`article-card-${article.slug}`}
                >
                  {/* Featured Image */}
                  {article.featured_image && (
                    <div className="aspect-video bg-[#F9FAFB] overflow-hidden">
                      <img 
                        src={article.featured_image} 
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  )}
                  
                  <div className="p-5">
                    {/* Category Badge */}
                    <span className="text-[11px] font-bold text-[#2F80ED] uppercase tracking-wide">
                      {article.category}
                    </span>
                    
                    <h2 className="text-[17px] font-bold text-[#102A43] mt-2 mb-2 group-hover:text-[#2F80ED] line-clamp-2">
                      {article.title}
                    </h2>
                    
                    <p className="text-[13px] text-[#64748B] mb-4 line-clamp-2">
                      {article.meta_description}
                    </p>
                    
                    <span className="text-[#2F80ED] font-medium text-[13px] flex items-center gap-1">
                      Read more <ArrowRight size={14} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-2 mt-12">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-4 py-2 rounded-md border border-[#E2E8F0] text-[14px] text-[#64748B] hover:border-[#2F80ED] hover:text-[#2F80ED] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`w-10 h-10 rounded-md text-[14px] font-medium transition-colors ${
                      currentPage === pageNum
                        ? 'bg-[#2F80ED] text-white'
                        : 'border border-[#E2E8F0] text-[#64748B] hover:border-[#2F80ED] hover:text-[#2F80ED]'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-4 py-2 rounded-md border border-[#E2E8F0] text-[14px] text-[#64748B] hover:border-[#2F80ED] hover:text-[#2F80ED] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}

          {/* CTA Section */}
          <div className="mt-16 bg-[#102A43] rounded-[10px] p-8 text-center">
            <h2 className="text-[24px] font-bold text-white mb-3">
              Check your AI accountability posture
            </h2>
            <p className="text-[16px] text-[#94A3B8] mb-6">
              Get your free AI Accountability Score in under 2 minutes.
            </p>
            <Link 
              to="/assessment/form"
              className="inline-flex items-center gap-2 bg-[#2F80ED] text-white font-semibold px-6 py-3 rounded-[7px] hover:bg-[#2563eb] transition-colors"
              data-testid="articles-cta-btn"
            >
              Get Your Free Score
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </main>

      <MainFooter />
    </div>
  );
}

export default Articles;
